// Phase 600 — Shared Task Details dialog.
// Used by Dashboard (cross-building shortcut) and BuildingDetails
// (per-building task panel). Same source of truth: /api/tasks/{id}.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDateDMY = (s) => {
  if (!s) return '';
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  } catch { return s; }
};

export default function TaskDetailsDialog({ open, taskId, initialTask, t, onClose, onChanged }) {
  const [task, setTask] = useState(initialTask || null);
  const [loading, setLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const reload = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tasks/${taskId}`);
      setTask(res.data);
    } catch { /* keep current */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && taskId) {
      setTask(initialTask ? { ...initialTask, history: initialTask.history || [] } : null);
      setNoteDraft('');
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  const handleAddNote = async (e) => {
    e?.preventDefault?.();
    const note = (noteDraft || '').trim();
    if (!note || !task?.task_id) return;
    try {
      await axios.post(`${API}/tasks/${task.task_id}/notes`, { note });
      setNoteDraft('');
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (t && t('errorOccurred')) || 'Error');
    }
  };

  const updateStatus = async (newStatus) => {
    if (!task?.task_id || task.status === newStatus) return;
    try {
      await axios.put(`${API}/tasks/${task.task_id}`, { status: newStatus });
      toast.success((t && t('savedSuccess')) || 'Saved');
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (t && t('errorOccurred')) || 'Error');
    }
  };

  const updateReminderDate = async (v) => {
    if (!task?.task_id) return;
    setTask(prev => ({ ...prev, reminder_date: v }));
    try {
      await axios.put(`${API}/tasks/${task.task_id}`, { reminder_date: v });
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (t && t('errorOccurred')) || 'Error');
    }
  };

  const statusButtons = [
    { key: 'pending',     label: (t && t('statusPending')) || 'Pending',     tone: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
    { key: 'in_progress', label: (t && t('statusInProgress')) || 'In Progress', tone: 'bg-blue-50 text-blue-700 border-blue-300' },
    { key: 'waiting',     label: (t && t('statusWaiting')) || 'Waiting',     tone: 'bg-amber-50 text-amber-700 border-amber-300' },
    { key: 'completed',   label: (t && t('statusCompleted')) || 'Completed', tone: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  ];

  const isCompleted = task?.status === 'completed';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-lg rounded-none max-h-[85vh] overflow-y-auto" data-testid="shared-task-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{(t && t('task')) || 'Task'}</span>
            {isCompleted && (
              <span className="text-[10px] font-semibold px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">{(t && t('completed')) || 'Completed'}</span>
            )}
            {task?.is_important && (
              <span className="text-[10px] font-semibold px-2 py-0.5 bg-red-50 text-red-700 border border-red-200">{(t && t('important')) || 'Important'}</span>
            )}
          </DialogTitle>
        </DialogHeader>
        {task ? (
          <div className="space-y-4">
            <div className="bg-zinc-50 border border-zinc-200 p-3 text-xs space-y-1">
              <div><span className="text-zinc-400">{(t && t('building')) || 'Building'}:</span> <span className="font-semibold text-zinc-800">{task.building_name || ''}</span></div>
              <div><span className="text-zinc-400">{(t && t('scope')) || 'Scope'}:</span>{' '}
                {task.type === 'apartment' ? (
                  <span className="font-semibold text-blue-700">{(t && t('apartment')) || 'Apartment'}{task.apartment_number ? ` #${task.apartment_number}` : ''}</span>
                ) : <span className="font-semibold text-zinc-800">{(t && t('building')) || 'Building'}</span>}
              </div>
              {task.start_date && <div><span className="text-zinc-400">{(t && t('startDate')) || 'Start Date'}:</span> <span className="font-mono">{fmtDateDMY(task.start_date)}</span></div>}
              {task.reminder_date && <div><span className="text-zinc-400">{(t && t('reminderDate')) || 'Reminder'}:</span> <span className="font-mono">{fmtDateDMY(task.reminder_date)}</span></div>}
              {task.notes && <div><span className="text-zinc-400">{(t && t('notes')) || 'Notes'}:</span> <span className="text-zinc-700">{task.notes}</span></div>}
            </div>

            <div>
              <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">{(t && t('history')) || 'History'}</h4>
              {(task.history || []).length === 0 ? (
                <p className="text-xs text-zinc-400 italic" data-testid="shared-task-history-empty">{(t && t('noHistoryYet')) || 'No history yet'}</p>
              ) : (
                <ul className="space-y-2 border-l-2 border-zinc-200 pl-3" data-testid="shared-task-history-list">
                  {(task.history || []).map(h => (
                    <li key={h.entry_id} className="relative" data-testid={`shared-task-history-${h.entry_id}`}>
                      <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 border-white ${h.type === 'status_change' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                      <div className="text-xs">
                        <div className="text-zinc-800">{h.note}</div>
                        <div className="text-[10px] text-zinc-400 mt-0.5">
                          <span>{h.author_name || 'System'}</span>
                          <span className="mx-1">·</span>
                          <span className="font-mono">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {!isCompleted && (
              <form onSubmit={handleAddNote} className="space-y-2 border-t border-zinc-200 pt-3">
                <Label className="text-xs">{(t && t('addNote')) || 'Add Note'}</Label>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={2}
                  className="w-full border border-zinc-300 rounded-none px-3 py-2 text-sm resize-none"
                  placeholder={(t && t('addNotePlaceholder')) || 'Type a comment, update, or log entry…'}
                  data-testid="shared-task-add-note-input"
                />
                <div className="flex justify-end">
                  <Button type="submit" disabled={!noteDraft.trim()} className="bg-zinc-950 text-white rounded-none" data-testid="shared-task-add-note-submit">{(t && t('addNote')) || 'Add Note'}</Button>
                </div>
              </form>
            )}

            <div className="border-t border-zinc-200 pt-3 space-y-3" data-testid="shared-task-status-controls">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">{(t && t('status')) || 'Status'}</Label>
                <div className="mt-1.5 grid grid-cols-4 gap-1.5" role="tablist">
                  {statusButtons.map(s => {
                    const active = (task.status || 'pending') === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => updateStatus(s.key)}
                        className={`text-[11px] px-2 py-1 border transition-colors ${active ? s.tone + ' font-semibold' : 'border-zinc-200 text-zinc-500 hover:bg-zinc-50'}`}
                        data-testid={`shared-task-status-${s.key}`}
                      >{s.label}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">{(t && t('followUpDate')) || 'Follow-up date'}</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input
                    type="date"
                    value={task.reminder_date || ''}
                    onChange={(e) => updateReminderDate(e.target.value || null)}
                    className="border border-zinc-300 rounded-none px-2 py-1 text-xs font-mono"
                    data-testid="shared-task-reminder-date-input"
                  />
                  {task.reminder_date && (
                    <button
                      type="button"
                      onClick={() => updateReminderDate(null)}
                      className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-zinc-900 px-1"
                      data-testid="shared-task-reminder-date-clear"
                    >{(t && t('clear')) || 'Clear'}</button>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="border-t border-zinc-200 pt-3">
              {!isCompleted ? (
                <Button type="button" onClick={() => updateStatus('completed')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none" data-testid="shared-task-mark-completed">{(t && t('markAsCompleted')) || 'Mark as Completed'}</Button>
              ) : (
                <Button type="button" onClick={() => updateStatus('in_progress')} variant="outline" className="rounded-none" data-testid="shared-task-reopen">{(t && t('reopenTask')) || 'Reopen Task'}</Button>
              )}
              <Button type="button" variant="outline" onClick={() => onClose?.()} className="rounded-none" data-testid="shared-task-close">{(t && t('close')) || 'Close'}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="text-center py-6 text-zinc-400 text-sm">{(t && t('loading')) || 'Loading'}…</div>
        )}
        {loading && <div className="text-[10px] text-zinc-400">{(t && t('loading')) || 'Loading'}…</div>}
      </DialogContent>
    </Dialog>
  );
}
